import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { setLang } from "../i18n";
import { buildPath, LANGUAGES, parseRoute, samePageIn } from "../lib/route";
import LogoMark from "./LogoMark";

function effectiveTheme(): "light" | "dark" {
  const t = document.documentElement.getAttribute("data-theme");
  if (t === "light" || t === "dark") return t;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function storedTheme(): string | null {
  try {
    return localStorage.getItem("theme");
  } catch {
    return null;
  }
}

export default function TopBar() {
  const { t, i18n } = useTranslation();
  const [theme, setTheme] = useState<"light" | "dark">(() =>
    typeof document === "undefined" ? "light" : effectiveTheme(),
  );

  // Follow the OS light/dark preference until the user makes an explicit choice.
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      if (!storedTheme()) setTheme(mq.matches ? "dark" : "light");
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    document.documentElement.setAttribute("data-theme", next);
    try {
      localStorage.setItem("theme", next);
    } catch {
      /* ignore */
    }
  };

  const lang = i18n.language.startsWith("en") ? "en" : "da";
  // Both language variants of the current page are real URLs, so the switcher is
  // a pair of links: crawlers can follow them, and a reader can copy one.
  const route = parseRoute(window.location.pathname);

  return (
    <header className="sticky top-0 z-40 border-b border-line backdrop-blur-md" style={{ background: "color-mix(in srgb, var(--canvas) 80%, transparent)" }}>
      <div className="mx-auto flex h-[68px] max-w-wrap items-center justify-between px-7">
        {/* The wordmark links home. It is inert on the front page itself, but the
            provider pages are real page loads, so people need the usual way back. */}
        <a
          href={buildPath("home", lang)}
          className="flex items-center gap-2.5 font-ui text-[21px] font-semibold tracking-[-0.02em] text-ink"
        >
          <span className="grid h-[30px] w-[30px] place-items-center rounded-[9px] bg-ink">
            <LogoMark className="h-[19px] w-auto text-accent" />
          </span>
          <span>Biloplader<span className="text-accent-deep">.dk</span></span>
          <span className="rounded-full border border-line-strong px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-ink-faint" title={t("a11y.beta")}>
            beta
          </span>
        </a>

        <div className="flex items-center gap-3.5">
          <div className="flex overflow-hidden rounded-full border border-line font-mono text-xs">
            {LANGUAGES.map((l) => (
              <a
                key={l}
                href={samePageIn(route, l)}
                hrefLang={l}
                aria-current={lang === l ? "true" : undefined}
                aria-label={t("a11y.lang", { lang: l === "da" ? "dansk" : "English" })}
                onClick={(e) => {
                  // Store the preference before the browser follows the link, so a
                  // later visit to a bare URL lands in the chosen language.
                  e.preventDefault();
                  setLang(l);
                }}
                className={`px-3 py-1.5 uppercase tracking-wide transition ${lang === l ? "bg-ink text-accent" : "text-ink-soft"}`}
              >
                {l}
              </a>
            ))}
          </div>
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={t("a11y.theme")}
            className="grid h-11 w-11 place-items-center rounded-full border border-line-strong bg-surface text-ink transition hover:-rotate-12 hover:border-accent"
          >
            {theme === "dark" ? (
              <svg viewBox="0 0 24 24" className="h-[19px] w-[19px]" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" /></svg>
            ) : (
              <svg viewBox="0 0 24 24" className="h-[19px] w-[19px]" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
            )}
          </button>
        </div>
      </div>
    </header>
  );
}
